import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, ShieldCheck, CircleDot } from "lucide-react";
import { api, errMsg, formatDate, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { RatingInput, Stars } from "@/components/Stars";
import { PageLoader } from "@/components/layout/RequireAuth";

const HELD = ["dibayar", "dikerjakan", "menunggu_konfirmasi", "dikomplain"];
const ACTIONS = {
  customer: { menunggu_konfirmasi: [["confirm", "Konfirmasi Selesai"]] },
  provider: { dibayar: [["start", "Mulai Kerjakan"]], dikerjakan: [["finish", "Pekerjaan Selesai"]] },
};

const Timeline = ({ items }) => (
  <ol className="space-y-5" data-testid="order-timeline">
    {items.map((t, i) => (
      <li key={i} className="relative flex gap-4" data-testid={`timeline-${t.status}`}>
        <CircleDot className={`mt-0.5 h-4 w-4 shrink-0 ${i === items.length - 1 ? "text-teal-700" : "text-stone-300"}`} />
        <div>
          <StatusBadge status={t.status} testId={`timeline-badge-${i}`} className="py-0.5" />
          <p className="mt-1 text-sm text-stone-700">{t.note}</p>
          <p className="text-xs text-stone-400">{formatDate(t.at, { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
        </div>
      </li>
    ))}
  </ol>
);

const ComplainDialog = ({ open, onOpenChange, onSubmit }) => {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>Laporkan Masalah</DialogTitle>
          <DialogDescription>Dana tetap ditahan Tuntas sampai tim kami meninjau laporan Anda.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Jelaskan masalah yang terjadi (min. 10 karakter)" className="rounded-xl" data-testid="complain-reason-input" />
        <Button onClick={() => onSubmit(reason)} className="rounded-xl bg-rose-700 hover:bg-rose-800" data-testid="complain-submit-button">Kirim Laporan</Button>
      </DialogContent>
    </Dialog>
  );
};

const ReviewBox = ({ order, onDone }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  if (order.review) {
    return (
      <div className="card-soft space-y-2 p-6" data-testid="order-review-display">
        <p className="font-bold text-stone-900">Ulasan Anda</p>
        <Stars value={order.review.rating} />
        <p className="text-sm text-stone-600">{order.review.comment}</p>
      </div>
    );
  }
  const submit = async () => {
    try {
      await api.post(`/orders/${order.order_id}/review`, { rating, comment });
      toast.success("Terima kasih atas ulasan Anda");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-soft space-y-4 p-6" data-testid="review-form">
      <p className="font-bold text-stone-900">Beri ulasan untuk {order.provider_name}</p>
      <RatingInput value={rating} onChange={setRating} testId="review-rating" />
      <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ceritakan pengalaman Anda…" className="rounded-xl" data-testid="review-comment-input" />
      <Button onClick={submit} disabled={!rating || comment.trim().length < 5} className="rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="review-submit-button">Kirim Ulasan</Button>
    </div>
  );
};

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [complain, setComplain] = useState(false);
  const load = useCallback(() => api.get(`/orders/${id}`).then((r) => setO(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, body) => {
    try {
      const { data } = await api.post(`/orders/${id}/actions/${action}`, body || {});
      setO(data);
      setComplain(false);
      toast.success("Status pesanan diperbarui");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!o) return <PageLoader />;
  const isC = o.side === "customer";
  const actions = ACTIONS[o.side][o.status] || [];
  const canComplain = isC && ["dibayar", "dikerjakan", "menunggu_konfirmasi"].includes(o.status);

  return (
    <div className="container-page py-10 lg:py-14" data-testid="order-detail-page">
      <Link to="/pesanan" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-teal-700" data-testid="back-to-orders"><ArrowLeft className="h-4 w-4" /> Pesanan Saya</Link>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl" data-testid="order-title">{o.title}</h1>
        <StatusBadge status={o.status} testId="order-detail-status" />
      </div>
      <p className="mt-1 text-sm text-stone-500">No. {o.order_id.replace(/[^a-z0-9]/gi, "").slice(-6).toUpperCase()} · {o.source === "package" ? "Pesan Langsung (paket)" : "Dari penawaran"}</p>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <div className="card-soft grid gap-4 p-6 sm:grid-cols-2 sm:p-8">
            {[[isC ? "Penyedia" : "Pelanggan", isC ? o.provider_name : o.customer_name], ["Kategori", o.category_name], ["Kota", o.city_name],
              ["Jadwal", formatDate(o.scheduled_date)], ["Alamat", o.address], ["Catatan", o.notes || "-"]].map(([k, v]) => (
              <div key={k}><p className="text-xs text-stone-500">{k}</p><p className="mt-0.5 whitespace-pre-line text-sm font-semibold text-stone-900">{v}</p></div>
            ))}
            {o.complaint_reason && <p className="rounded-xl bg-rose-50 p-3 text-sm text-rose-800 sm:col-span-2" data-testid="order-complaint">Laporan masalah: {o.complaint_reason}</p>}
          </div>
          <div className="card-soft p-6 sm:p-8">
            <h2 className="mb-5 text-lg font-bold text-stone-900">Riwayat status</h2>
            <Timeline items={o.timeline} />
          </div>
          {isC && o.status === "selesai" && <ReviewBox order={o} onDone={load} />}
        </div>
        <aside className="space-y-4">
          <div className="card-soft space-y-4 p-6">
            <p className="text-sm text-stone-500">Total</p>
            <p className="text-3xl font-extrabold text-stone-900" data-testid="order-price">{rupiah(o.price)}</p>
            {!isC && o.commission != null && (
              <div className="space-y-1 rounded-xl bg-stone-50 p-3 text-sm" data-testid="order-commission-breakdown">
                <p className="flex justify-between"><span>Komisi Tuntas ({o.commission_percent}%)</span><span>- {rupiah(o.commission)}</span></p>
                <p className="flex justify-between font-bold"><span>Pendapatan bersih</span><span>{rupiah(o.provider_net)}</span></p>
              </div>
            )}
            {HELD.includes(o.status) && <p className="flex items-center gap-2 rounded-xl bg-sky-50 p-3 text-xs text-sky-900" data-testid="order-funds-held"><ShieldCheck className="h-4 w-4" /> Dana Ditahan oleh Tuntas</p>}
            {o.status === "selesai" && <p className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="order-funds-released"><ShieldCheck className="h-4 w-4" /> Dana diteruskan ke penyedia (siap dicairkan)</p>}
            {isC && o.status === "menunggu_pembayaran" && (
              <>
                <Button asChild className="h-11 w-full rounded-xl bg-teal-700 font-semibold hover:bg-teal-800" data-testid="order-pay-button"><Link to={`/bayar?pesanan=${o.order_id}`}>Bayar Sekarang</Link></Button>
                <Button variant="outline" onClick={() => act("cancel")} className="w-full rounded-xl text-rose-700 hover:bg-rose-50" data-testid="order-cancel-button">Batalkan Pesanan</Button>
              </>
            )}
            {actions.map(([a, label]) => (
              <Button key={a} onClick={() => act(a)} className="h-11 w-full rounded-xl bg-teal-700 font-semibold hover:bg-teal-800" data-testid={`order-action-${a}`}>{label}</Button>
            ))}
            {canComplain && <Button variant="ghost" onClick={() => setComplain(true)} className="w-full rounded-xl text-rose-700 hover:bg-rose-50" data-testid="order-complain-button">Laporkan Masalah</Button>}
            {!isC && o.status === "menunggu_pembayaran" && <p className="text-xs text-stone-500">Menunggu pelanggan menyelesaikan pembayaran.</p>}
          </div>
          <div className="card-soft space-y-3 p-6">
            {o.thread_id && (
              <Button asChild variant="outline" className="w-full rounded-xl" data-testid="order-chat-button"><Link to={`/pesan/${o.thread_id}`}><MessageSquare className="mr-2 h-4 w-4" /> Chat</Link></Button>
            )}
            <WhatsAppButton whatsapp={o.whatsapp} testId="order-whatsapp-button" fallback={o.status === "menunggu_pembayaran" ? "Tombol WhatsApp muncul setelah pembayaran." : "Nomor WhatsApp belum tersedia."} />
          </div>
        </aside>
      </div>
      <ComplainDialog open={complain} onOpenChange={setComplain} onSubmit={(reason) => act("complain", { reason })} />
    </div>
  );
}
